import { useEffect, useMemo, useState } from "react";
import { redirect, RedirectFunction } from "react-router";
import { Paths } from "Shared/Utils";

import { BrowserCacheLocation, Configuration, NavigationClient, NavigationOptions, PublicClientApplication } from "@azure/msal-browser";

import { useAppSettings } from "../../Shared/Hooks";
import { useRootStore } from "Store/RootStore";
import { FakePublicClientApplication, isE2EFakeAuth } from "./FakePublicClientApplication";

export const usePublicClientApplication = () => {
    const { clientIdInMicrosoft, clientIdInTorus, aadUrl, microsoftTenantId } = useAppSettings();
    const authStore = useRootStore("AuthStore");
    const [isInitialized, setIsInitialized] = useState(false);
    const msalInstanceKey = `${authStore.LoginType}-${authStore.pcaInstanceVersion}`;

    const { clientIdSpecified, tenantIdSpecified } = useMemo(() => {
        const clientIdLocal = authStore.LoginType === "Gallatin" ? clientIdInMicrosoft : clientIdInTorus;
        const tenantIdLocal = authStore.LoginType === "Gallatin" ? microsoftTenantId : "common";
        return { clientIdSpecified: clientIdLocal, tenantIdSpecified: tenantIdLocal };
    }, [authStore.LoginType, clientIdInMicrosoft, clientIdInTorus, microsoftTenantId]);

    // Set the authority URL based on the login type

    const msalInstance = useMemo(() => {
        // Validate aadUrl is configured
        if (!aadUrl) {
            throw new Error("AAD URL is not configured. Please check application settings.");
        }

        // Normalize URLs: strip all trailing slashes from aadUrl and leading slashes from tenantId
        const normalizedAadUrl = aadUrl.replace(/\/+$/, "");
        const normalizedTenantId = (tenantIdSpecified ?? "").replace(/^\/+/, "");

        const msalConfig: Configuration = {
            auth: {
                clientId: clientIdSpecified ?? "",
                authority: `${normalizedAadUrl}/${normalizedTenantId}`,
                redirectUri: Paths.getFullPath("/redirect.html"),
                postLogoutRedirectUri: Paths.getFullPath("/login")
            },
            cache: {
                cacheLocation: BrowserCacheLocation.LocalStorage
            }
        };

        const instance = isE2EFakeAuth() ? new FakePublicClientApplication(msalConfig) : new PublicClientApplication(msalConfig);
        instance.setNavigationClient(new CustomNavigationClient(redirect));
        return instance;
    }, [aadUrl, clientIdSpecified, tenantIdSpecified]);

    useEffect(() => {
        const init = async () => {
            await msalInstance.initialize();
            await msalInstance.handleRedirectPromise();
            setIsInitialized(true);
        };
        setIsInitialized(false);
        init();
    }, [msalInstance]);

    return { msalInstance, isInitialized, msalInstanceKey };
};

class CustomNavigationClient extends NavigationClient {
    private redirect: RedirectFunction;

    constructor(redirectfunc: RedirectFunction) {
        super();
        this.redirect = redirectfunc;
    }

    public navigateInternal(url: string, options: NavigationOptions) {
        const relativePath = url.replace(window.location.origin + Paths.getBaseUri(), "");
        this.redirect(relativePath);

        return Promise.resolve(false);
    }
}
